import { getIndexForNode, getIndexForNodeTooltip, scrollable } from "@/common";
import { AttributeDescription, getAttributeDescription } from "@/common/AttributeDescription";
import { useFullRelease } from "@/common/hooks_useFullRelease";
import { useNotification } from "@/common/notifications";
import { useGraphVisualisation } from "@/components/GraphVisualisation/hooks/useGraphVisualisation";
import { Label } from "@/components/meta";
import { isFeatureEnabled } from "@/config";

import AttributeContext from "@/pages/models/release/AttributeContext"; // TOOD: should move
import { AttributeUsage } from "@/pages/models/release/DataModel/route/Attributes/DocumentUsage";
import styled from "@emotion/styled";
import AttributeExplanation from "@imminently/decisively-ui/AttributeExplanation";
import AttributeValue from "@imminently/decisively-ui/AttributeValue";
import Chip from "@imminently/decisively-ui/Chip";
import DetailTable from "@imminently/decisively-ui/DetailTable";
import Icon from "@imminently/decisively-ui/Icon";
import Button from "@imminently/decisively-ui/Button";
import NodePath from "@imminently/decisively-ui/NodePath";
import Tabs from "@imminently/decisively-ui/Tabs";
import { Card, CardContent, CardHeader, Tooltip, Typography, useTheme } from "@mui/material";
import { filter, get } from "lodash";
import { useTranslation } from "react-i18next";
import NodeInputs from "./NodeInputs";

const AttributeDetail = styled(Card)`
  height: 100%;
  overflow: overlay;
`;

const Container = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  overflow: overlay;
  gap: 1.75rem;
  padding: 1rem 0;
`;

export interface AttributeInfoProps {
  showInputValues?: boolean;
  onClose?: () => void;
  showTitle?: boolean;
  showClose?: boolean;
}

export const Index = ({ value, hint, identifier }) => {
  // React components don't return undefined
  if (typeof value === "undefined") return null;
  if (typeof hint !== "undefined") {
    return (
      <Tooltip
        title={getIndexForNodeTooltip({ hint, identifier, value })}
        style={{ display: "inline" }}
      >
        <div>
          <Chip>{value}</Chip>
        </div>
      </Tooltip>
    );
  } else {
    return (
      <>
        <Chip>{value}</Chip>
      </>
    );
  }
};

const AttributeInfo = (props: AttributeInfoProps) => {
  const { showInputValues, onClose, showTitle, showClose } = props;
  const { selectedNode, setSelectedNodeId, debug, graph } = useGraphVisualisation();
  const release = useFullRelease();
  const notify = useNotification();
  const theme = useTheme();
  const { t } = useTranslation();
  if (!selectedNode) return null;

  const onDocumentClick = () => {
    onClose?.();
  };

  const resolvedShowInputValues = showInputValues ?? true;
  const resolvedShowTitle = showTitle ?? true;
  const resolvedShowClose = showClose ?? true;

  const indexInfo: any = getIndexForNode(selectedNode, graph) || {};
  const showReason = isFeatureEnabled("experimental.extended_debug_diagnostics");

  return (
    <>
      <AttributeDetail className={scrollable}>
        {resolvedShowTitle && (
          <CardHeader
            title={`${t("attribute")}: '${getAttributeDescription(selectedNode)}'`}
            action={
              resolvedShowClose && (
                <Button icon
                  label="Close"
                  onClick={() => {
                    setSelectedNodeId(null);
                    if (onClose) onClose();
                  }}
                >
                  <Icon name="close" />
                </Button>
              )
            }
          />
        )}
        <CardContent>
          <Tabs defaultValue="rules">
            <Tabs.List className="border-b border-border">
              <Tabs.Trigger value="rules">{t("explanation.rules")}</Tabs.Trigger>
              <Tabs.Trigger
                value="explanation"
                disabled={!selectedNode.explanation}
              >
                {t("explanation.explanation")}
              </Tabs.Trigger>
            </Tabs.List>
            <Tabs.Content value="rules">
              <Container className={scrollable}>
                <DetailTable className="mb-4">
                  {debug && (
                    <DetailTable.Item>
                      <DetailTable.Icon>
                        <Icon name="equal" />
                      </DetailTable.Icon>
                      <DetailTable.Title>{t("explanation.value")}</DetailTable.Title>
                      <DetailTable.Content>
                        <AttributeValue
                          node={selectedNode}
                          showReason={showReason}
                        />
                      </DetailTable.Content>
                    </DetailTable.Item>
                  )}
                  {debug && selectedNode.justification && (
                    <DetailTable.Item>
                      <DetailTable.Icon>
                        <Icon name="documents" />
                      </DetailTable.Icon>
                      <DetailTable.Title>{t("explanation.justification")}</DetailTable.Title>
                      <DetailTable.Content>{selectedNode.justification}</DetailTable.Content>
                    </DetailTable.Item>
                  )}
                  <DetailTable.Item>
                    <DetailTable.Icon>
                      <Icon name="account" />
                    </DetailTable.Icon>
                    <DetailTable.Title>{t("entity")}</DetailTable.Title>
                    <DetailTable.Content>
                      <div className="flex items-center gap-2">
                        <Chip backgroundColor={(theme as any)?.labels?.public}>{selectedNode.entity}</Chip>
                        {selectedNode.entity !== "global" && (
                          <span>
                            {" "}
                            (<Index {...indexInfo} />)
                          </span>
                        )}
                      </div>
                    </DetailTable.Content>
                  </DetailTable.Item>
                  {selectedNode.path && selectedNode.path.split("/").length > 3 && (
                    <DetailTable.Item>
                      <DetailTable.Icon>
                        <Icon name="target" />
                      </DetailTable.Icon>
                      <DetailTable.Title>Contained by</DetailTable.Title>
                      <DetailTable.Content>
                        <NodePath
                          node={{
                            path: selectedNode.path.split("/").slice(0, -2).join("/"),
                          }}
                          showDescription={false}
                        />
                      </DetailTable.Content>
                    </DetailTable.Item>
                  )}
                  <AttributeUsage
                    node={selectedNode}
                    onClick={onDocumentClick}
                    noContainer
                    noDuplicates
                  />
                </DetailTable>

                <div>
                  <Typography
                    variant="h5"
                    style={{ marginBottom: "0.5rem" }}
                  >
                    {t("explanation.how_calculated_title")}
                  </Typography>
                  <AttributeContext.Provider
                    value={(node) => {
                      console.log("click", node);
                      if (debug && node.hidden) {
                        // See how many instances of this
                        const others = filter(graph._nodes, (n) => {
                          if (n.id === node.id && !n.hidden) return true;
                          else return false;
                        });
                        if (others.length === 1) setSelectedNodeId(others[0].path);
                        else {
                          notify("Can't determine which instance of this attribute to navigate to");
                        }
                      } else setSelectedNodeId(node.path || node.id);
                    }}
                  >
                    <AttributeExplanation
                      node={selectedNode}
                      selectedNode={selectedNode}
                    />
                  </AttributeContext.Provider>
                </div>
                {debug && resolvedShowInputValues && (
                  <div>
                    <Typography variant="h5">{t("explanation.input_values")}</Typography>
                    <NodeInputs node={selectedNode} />
                  </div>
                )}
              </Container>
            </Tabs.Content>
            <Tabs.Content value="explanation">
              <Container className={scrollable}>
                <Typography variant="h4">{t("explanation.explanation")}</Typography>
                {selectedNode.explanation}
              </Container>
            </Tabs.Content>
          </Tabs>
        </CardContent>
      </AttributeDetail>
    </>
  );
};

export const Explanation = ({ attribute, setCurrentAttr }) => {
  const { t } = useTranslation();
  const release = useFullRelease();
  if (!attribute) {
    return <span>{t("errors.error_reading_attribute")}</span>;
  }

  return (
    <>
      {attribute.reference ? (
        <Typography>
          {t("explanation.reference")}: {get(attribute, "reference.title")} ({get(attribute, "reference.document")}:
          {get(attribute, "reference.line")})
        </Typography>
      ) : (
        get(attribute, "definedIn") && (
          <Typography>
            {t("defined_in")}: {get(attribute, "definedIn")}
          </Typography>
        )
      )}
      <Typography variant="h5">{t("explanation.explanation")}</Typography>
      <AttributeContext.Provider value={setCurrentAttr}>
        <AttributeExplanation node={attribute} />
      </AttributeContext.Provider>
      <Typography variant="h5">{t("used_in")}</Typography>
      <ul>
        {get(attribute, "usedIn", []).map((used, index) => (
          <li key={index}>{used}</li>
        ))}
      </ul>
    </>
  );
};

export default AttributeInfo;
